import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  TruckIcon,
  UserIcon,
  MapPinIcon,
  ClockIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PhoneIcon,
  StarIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline'
import { ambulanceBookingService, BookingStatus } from '../../services/ambulance-booking.service'
import { reviewService, ReviewData } from '../../services/review.service'
import { formatShortDate, formatStatus, formatCurrency } from '../../utils/formatters'
import LoadingSpinner from '../../components/LoadingSpinner'
import ReviewForm from '../../components/reviews/ReviewForm'

function AmbulanceBookingDetailsPage() {
  const { bookingId } = useParams<{ bookingId: string }>()
  const navigate = useNavigate()
  const [booking, setBooking] = useState<BookingStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [review, setReview] = useState<ReviewData | null>(null)
  const [isReviewFormOpen, setIsReviewFormOpen] = useState(false)
  const [isSubmittingReview, setIsSubmittingReview] = useState(false)

  const isPaymentPending = () => {
    if (!booking) return false;

    // First, check if payment is already completed - exclude these
    if (booking.payment?.status === 'PAID' ||
        booking.payment?.status === 'VERIFIED' ||
        booking.payment?.status === 'COMPLETED') {
      return false;
    }

    // Check if payment is submitted and awaiting verification - exclude these too
    if (booking.payment?.status === 'PENDING' &&
        (booking.workflow?.currentStep === 'PAYMENT_SUBMITTED' ||
         booking.workflow?.nextStep === 'AWAITING_PAYMENT_VERIFICATION' ||
         booking.workflow?.currentStep?.toLowerCase().includes('verification'))) {
      return false;
    }

    // Check if paymentStatus exists and indicates pending (but not submitted)
    if (booking.payment?.status === 'PENDING') {
      // Check if this is initial payment pending (not submitted yet)
      const currentStep = booking.workflow?.currentStep || '';
      const nextStep = booking.workflow?.nextStep || '';

      // Only return true if we're still at the initial payment stage
      if (currentStep === 'Booking Created' &&
          (nextStep === 'Awaiting Payment' || nextStep.toLowerCase().includes('payment'))) {
        return true;
      }
    }

    // Check if workflow status indicates payment is pending (but not submitted)
    const currentStep = booking.workflow?.currentStep || '';
    const nextStep = booking.workflow?.nextStep || '';

    if (nextStep.toLowerCase().includes('payment') ||
        currentStep.toLowerCase().includes('awaiting payment') ||
        currentStep === 'Awaiting Payment') {
      // Make sure we're not in the verification stage
      if (!currentStep.toLowerCase().includes('verification') &&
          !nextStep.toLowerCase().includes('verification')) {
        return true;
      }
    }

    // Check if booking status itself indicates payment pending
    if (booking.booking?.status === 'PENDING_PAYMENT') {
      return true;
    }

    return false;
  }

  useEffect(() => {
    if (!bookingId) {
      setError('Booking ID is required')
      setIsLoading(false)
      return
    }

    const fetchBookingDetails = async () => {
      try {
        const bookingDetails = await ambulanceBookingService.getBookingStatus(bookingId)
        setBooking(bookingDetails)

        // Fetch review if booking exists
        if (bookingDetails) {
          try {
            const reviewData = await reviewService.getReviewByBookingId(bookingId)
            setReview(reviewData)
          } catch (reviewError) {
            // Review not found is normal, just ignore
            console.log('No review found for this booking')
          }
        }
      } catch (err) {
        console.error('Failed to fetch booking details:', err)
        setError('Failed to load booking details. Please try again later.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchBookingDetails()
  }, [bookingId])

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
      default:
        return <ClockIcon className="h-5 w-5 text-blue-500" />
    }
  }

  const handleSubmitReview = async (rating: number, comment?: string) => {
    if (!bookingId) return;

    setIsSubmittingReview(true);
    try {
      const newReview = await reviewService.createReview(bookingId, { rating, comment });
      setReview(newReview);
    } catch (error) {
      console.error('Failed to submit review:', error);
      throw error;
    } finally {
      setIsSubmittingReview(false);
    }
  }

  const isCompletedAndNotReviewed = booking?.booking.status === 'COMPLETED' && !review;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !booking) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error || 'Booking not found'}</p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Link
            to="/bookings"
            className="btn-outline"
          >
            Back to Bookings
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/bookings"
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Booking Details
              </h1>
              <p className="text-sm text-gray-600">
                Booking ID: {booking.booking.id}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(booking.booking.status)}
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              booking.booking.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
              booking.booking.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {formatStatus(booking.booking.status)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Booking Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Trip Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <TruckIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Trip Information</h2>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trip Type</label>
                  <p className="text-gray-900">{booking.booking.tripType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ambulance Type</label>
                  <p className="text-gray-900">{booking.booking.ambulanceType}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
                <div className="flex items-start space-x-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                  <p className="text-gray-900">{booking.booking.originAddress}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
                <div className="flex items-start space-x-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                  <p className="text-gray-900">{booking.booking.destinationAddress}</p>
                </div>
              </div>
              {booking.booking.scheduledDateTime && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time</label>
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">
                      {new Date(booking.booking.scheduledDateTime).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Provider Information */}
          {booking.provider && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <UserIcon className="h-5 w-5 text-gray-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Provider Information</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <p className="text-gray-900">{booking.provider.companyName}</p>
                </div>
                {booking.provider.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                      <p className="text-gray-900">{booking.provider.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Case Information */}
          {booking.case && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Case Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case Number</label>
                  <p className="text-gray-900">{booking.case.caseNumber}</p>
                </div>
                {booking.case.deceasedName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deceased Name</label>
                    <p className="text-gray-900">{booking.case.deceasedName}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Status & Payment */}
        <div className="space-y-6">
          {/* Booking Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Booking Status</h3>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium">{booking.workflow.progress}%</span>
                </div>
                <div className="mt-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${booking.workflow.progress}%` }}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Step</p>
                <p className="text-sm font-medium text-gray-900">{formatStatus(booking.workflow.currentStep)}</p>
              </div>
              {booking.workflow.nextStep && (
                <div>
                  <p className="text-sm text-gray-600">Next Step</p>
                  <p className="text-sm font-medium text-gray-900">{formatStatus(booking.workflow.nextStep)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <BanknotesIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Payment Information</h3>
            </div>
            <div className="space-y-4">
              {booking.booking.pricing && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(booking.booking.pricing.total)}
                    </p>
                  </div>
                  {booking.booking.pricing.breakdown && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price Breakdown</label>
                      <p className="text-xs text-gray-600">{booking.booking.pricing.breakdown}</p>
                    </div>
                  )}
                </>
              )}
              {/* Always show payment status section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <div className="flex items-center gap-2">
                {booking.payment ? (
                  <>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      booking.payment.status === 'PAID' || booking.payment.status === 'VERIFIED' || booking.payment.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {booking.payment.status === 'PAID' || booking.payment.status === 'VERIFIED' || booking.payment.status === 'COMPLETED'
                        ? 'Paid'
                        : 'Pending'}
                    </span>
                  </>
                ) : (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                )}
                  </div>
            </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              {isPaymentPending() && (
                <Link
                  to={`/bookings/${bookingId}/payment`}
                  className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Complete Payment
                </Link>
              )}

              <Link
                to="/bookings"
                className="block w-full text-center bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Back to All Bookings
              </Link>

              {/* Rate Service Button - Only show for completed bookings without existing review */}
              {isCompletedAndNotReviewed && (
                <button
                  onClick={() => setIsReviewFormOpen(true)}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  <StarIcon className="h-5 w-5 mr-2" />
                  Rate Service
                </button>
              )}

              {booking.booking.status === 'PENDING' && (
                <button className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                  Cancel Booking
                </button>
              )}
            </div>
          </div>

          {/* Review Display Section - Only show if review exists */}
          {review && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Review</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        className={`h-5 w-5 ${
                          i < review.rating
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                        fill={i < review.rating ? 'currentColor' : 'none'}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-600">
                    {review.rating}/5
                  </span>
                </div>

                {review.comment && (
                  <div>
                    <p className="text-gray-700 italic">"{review.comment}"</p>
                  </div>
                )}

                <div className="text-xs text-gray-500">
                  Reviewed on {new Date(review.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Form Modal */}
      {isReviewFormOpen && (
        <ReviewForm
          isOpen={isReviewFormOpen}
          onClose={() => setIsReviewFormOpen(false)}
          onSubmit={handleSubmitReview}
          providerName={booking?.provider?.companyName || 'Service Provider'}
          isSubmitting={isSubmittingReview}
        />
      )}
    </div>
  )
}

export default AmbulanceBookingDetailsPage